import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  BookOpen,
  Building2,
  Check,
  ChevronsUpDown,
  HelpCircle,
  LogOut,
  Moon,
  Sun,
  User,
} from 'lucide-react';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/shared/store/auth.store';
import { logoutApi } from '@/lib/api/client';
import { useQueryClient } from '@tanstack/react-query';
import { useSwitchAppMutation } from '@/features/auth/queries/auth.queries';
import { toast } from 'sonner';
import type { AplicacionDto } from '@/shared/types/tenant';
import { IndiceToursDialog } from '@/features/guia-interactiva/components/indice-tours-dialog';

function getInitials(nombre: string): string {
  return nombre
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase();
}

export function AdminNavbar() {
  const user = useAuthStore(state => state.user);
  const activeApp = useAuthStore(state => state.activeApp);
  const availableApps = useAuthStore(state => state.availableApps);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const switchAppMutation = useSwitchAppMutation();
  const [indiceAbierto, setIndiceAbierto] = useState(false);

  async function handleLogout() {
    await logoutApi();
    navigate({ to: '/login' });
  }

  async function handleVerQueEsRecet() {
    // Force refetch fresh data from server, then override to show as tutorial
    await queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    await queryClient.refetchQueries({ queryKey: ['onboarding-status'] });
    queryClient.setQueryData(['onboarding-status'], (prev: unknown) => {
      if (!prev || typeof prev !== 'object') return prev;
      return {
        ...(prev as Record<string, unknown>),
        completado: false,
        esTutorial: true,
        esObligatorio: false,
        pasoActual: 1,
      };
    });
  }

  function handleSwitchApp(app: AplicacionDto) {
    if (activeApp?.codigo === app.codigo) return;
    switchAppMutation.mutate(
      { appCodigo: app.codigo },
      {
        onSuccess: () => {
          toast.success(`Cambiado a ${app.nombre}`);
        },
        onError: error => {
          toast.error(error.message || 'Error al cambiar aplicación');
        },
      }
    );
  }

  return (
    <header className="bg-background/95 flex h-14 shrink-0 items-center gap-2 border-b px-4 backdrop-blur-sm">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />

      <div className="flex-1" />

      {availableApps.length > 1 && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={switchAppMutation.isPending}
              >
                <Building2 className="size-4" />
                <span className="hidden max-w-[150px] truncate sm:inline-block">
                  {activeApp?.nombre ?? 'Seleccionar app'}
                </span>
                <ChevronsUpDown className="text-muted-foreground size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Aplicaciones</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {availableApps
                .sort((a, b) => a.orden - b.orden)
                .map(app => (
                  <DropdownMenuItem
                    key={app.id}
                    onClick={() => handleSwitchApp(app)}
                    className="flex items-center gap-2"
                  >
                    <Building2 className="text-muted-foreground size-4" />
                    <span className="flex-1 truncate">{app.nombre}</span>
                    {activeApp?.codigo === app.codigo && <Check className="text-primary size-4" />}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Separator orientation="vertical" className="h-4" />
        </>
      )}

      <Button
        variant="ghost"
        size="icon"
        className="size-9"
        onClick={() => {
          const isDark = document.documentElement.classList.toggle('dark');
          localStorage.setItem('admin-theme', isDark ? 'dark' : 'light');
        }}
      >
        <Sun className="size-4 dark:hidden" />
        <Moon className="hidden size-4 dark:block" />
        <span className="sr-only">Cambiar tema</span>
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="relative h-9 gap-2 rounded-full px-2"
            data-testid="navbar.user-menu"
          >
            <Avatar className="size-7">
              <AvatarImage src={user?.foto ?? undefined} alt={user?.nombreCompleto ?? ''} />
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                {getInitials(user?.nombreCorto ?? user?.nombres ?? 'U')}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium md:inline-block">
              {user?.nombreCorto ?? user?.nombres}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm leading-none font-medium">{user?.nombreCompleto}</p>
              <p className="text-muted-foreground text-xs leading-none">{user?.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={'/admin/perfil' as string}>
              <User className="mr-2 size-4" />
              Mi perfil
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleVerQueEsRecet}>
            <BookOpen className="mr-2 size-4" />
            ¿Qué es RECET?
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setIndiceAbierto(true)}>
            <HelpCircle className="mr-2 size-4" />
            Guías interactivas
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            className="text-destructive focus:text-destructive"
            data-testid="navbar.logout"
          >
            <LogOut className="mr-2 size-4" />
            Cerrar sesion
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <IndiceToursDialog open={indiceAbierto} onOpenChange={setIndiceAbierto} />
    </header>
  );
}
